"use client";

import { DeviceType, Language, TranslationScopes } from "@finbackoffice/enums";
import { ClientBFFContext, getTranslations, useRuntimeConfig } from "@finbackoffice/site-core";
import { useParams } from "next/navigation";
import { useContext, useEffect, useMemo, useState } from "react";
import { I18nContext } from "react-i18next";

export const useLoadI18nextResource = (
    values: TranslationScopes[],
    deviceType: DeviceType = DeviceType.Mobile,
): { isTranslationsReady: boolean } => {
    const VERSION = useRuntimeConfig("VERSION");
    const client = useContext(ClientBFFContext);
    const { lang: locale } = useParams();
    const { i18n } = useContext(I18nContext);
    const [isReady, setIsReady] = useState(false);

    useEffect(() => {
        setIsReady(false);
        async function load() {
            const scopes = values.filter(
                (value) => !i18n.hasResourceBundle(locale as string, value),
            );

            if (!scopes.length) {
                setIsReady(true);
                return;
            }

            const response = await getTranslations({
                client,
                request: {
                    language: locale as Language,
                    scopes,
                },
                deviceType,
                includeLocalTranslations: VERSION === "local",
            });

            scopes.forEach((scope) => {
                i18n.addResourceBundle(locale as string, scope, response[scope], true, true);
            });
            setIsReady(true);
        }

        void load();
    }, [i18n, locale, deviceType, client, VERSION, values]);

    return useMemo(() => ({ isTranslationsReady: isReady }), [isReady]);
};
